/**
 * Centralized JSON-LD structured data for Design Flow Digitals.
 *
 * These objects are the entity facts that search engines and AI answer
 * engines (ChatGPT, Perplexity, Google AI Overviews) extract and cite.
 * Keep them factual, complete, and consistent across the site.
 */

export const SITE_URL = 'https://designflowdigitals.com'
const LOGO =
  'https://cdn.magicpatterns.com/uploads/tEKY1KVFn8NPQae4HonbJW/9fea8157-abe6-4116-8e53-72149f139631.png'

/** Stable @id for the organization so other nodes can reference it. */
export const ORG_ID = `${SITE_URL}/#organization`
export const WEBSITE_ID = `${SITE_URL}/#website`

/**
 * Rich Organization / professional service graph. This is the single most
 * important block for GEO: it tells AI engines exactly who the company is,
 * who runs it, where it operates, and what it does.
 */
export const organizationSchema: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@type': ['Organization', 'ProfessionalService'],
  '@id': ORG_ID,
  name: 'Design Flow Digitals',
  alternateName: 'Design Flow Digitals Shopify Agency',
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: LOGO,
  },
  image: LOGO,
  description:
    'Design Flow Digitals is a premium Shopify development agency that has delivered over 1,000 Shopify stores. We specialize in custom Shopify themes, high-performance Shopify apps, full store builds, and conversion rate optimization for global merchants.',
  email: 'support@designflowdigitals.com',
  foundingDate: '2019',
  founder: {
    '@type': 'Person',
    name: 'Pankaj Maurya',
    jobTitle: 'Founder',
    description:
      'Founder of Design Flow Digitals. Full-stack Shopify developer with a Master of Commerce and a top-rated profile on Fiverr.',
    sameAs: ['http://fiverr.com/webpankaj'],
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '67 Wigley Loop',
    addressLocality: 'Whitwell',
    addressRegion: 'TN',
    postalCode: '37397',
    addressCountry: 'US',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@designflowdigitals.com',
    contactType: 'customer support',
    availableLanguage: ['English'],
  },
  areaServed: {
    '@type': 'Place',
    name: 'Worldwide',
  },
  knowsAbout: [
    'Shopify development',
    'Shopify theme development',
    'Shopify app development',
    'Ecommerce conversion rate optimization',
    'Custom Shopify stores',
    'Shopify SEO',
  ],
  slogan: 'Premium Shopify development for high-converting stores.',
  sameAs: [
    'https://www.shopify.com/partners/directory/partner/designflowdigitals',
    'https://apps.shopify.com/partners/designflowdigitals',
    'http://fiverr.com/webpankaj',
  ],
}

/** WebSite node with a search action and a link back to the organization. */
export const websiteSchema: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  name: 'Design Flow Digitals',
  url: SITE_URL,
  publisher: { '@id': ORG_ID },
  inLanguage: 'en-US',
}

/**
 * Build a BreadcrumbList for a page. Pass an ordered list of
 * { name, path } items from the site root down to the current page.
 */
export function breadcrumbSchema(
  items: { name: string; path: string }[],
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.path.startsWith('http')
        ? item.path
        : `${SITE_URL}${item.path.startsWith('/') ? '' : '/'}${item.path}`,
    })),
  }
}

/** Build a FAQPage node from question/answer pairs for citable AI answers. */
export function faqSchema(
  faqs: { question: string; answer: string }[],
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }
}

/** Build a Service node tied back to the organization as provider. */
export function serviceSchema(service: {
  name: string
  description: string
  path: string
  serviceType?: string
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    serviceType: service.serviceType ?? service.name,
    url: service.path.startsWith('http')
      ? service.path
      : `${SITE_URL}${service.path.startsWith('/') ? '' : '/'}${service.path}`,
    provider: { '@id': ORG_ID },
    areaServed: { '@type': 'Place', name: 'Worldwide' },
  }
}

/** Build a SoftwareApplication node for a Shopify app. */
export function softwareAppSchema(app: {
  name: string
  description: string
  path: string
  category?: string
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: app.name,
    description: app.description,
    applicationCategory: app.category ?? 'BusinessApplication',
    operatingSystem: 'Shopify',
    url: app.path.startsWith('http')
      ? app.path
      : `${SITE_URL}${app.path.startsWith('/') ? '' : '/'}${app.path}`,
    publisher: { '@id': ORG_ID },
    offers: {
      '@type': 'Offer',
      category: 'SaaS',
    },
  }
}
